import { GenderConst } from './constants/Gender.const';
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormArray, FormBuilder } from '@angular/forms';
import { PlannerService } from '../../services/planner.service';
import * as _ from 'lodash';
import { LocationStrategy } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';
import { PlanConfigVO } from '../../interfaces/vo/PlanConfigVO';
import { DataService } from '../../services/data.service';
import { ModalService } from 'src/app/services/modal.service';
import { PlannerConstants } from '../../constants/planner.const';
import { errorResponse } from '../../interfaces/ErrorResponse';
import { PlanStatus } from './constants/PlanStatus.const';
import { pruneEmpty } from 'src/app/util/util';
import { DigitalPlanConfigVO } from 'src/app/planner/interfaces/vo/DigitalPlanConfigVO';
import { FormTabNames, FormTabName } from './constants/FormTabNames.const';
import { DashboardType } from '../../enums/DashboardType.enum';
import { StorageService } from 'src/app/services/storage.service';
import { Constants } from 'src/app/core/enums/constants.enum';

@Component({
  selector: 'planner-edit',
  templateUrl: './planner-edit.component.html',
  styleUrls: ['./planner-edit.component.scss'],
  providers: [
    PlannerService
  ]
})
export class PlannerEditComponent implements OnInit {
  static readonly DigitalVideo: string = "DigitalVideo";
  planForm: FormGroup;
  hasDigitalVideo: boolean = true;
  formTitle: string;
  tabNames: Array<FormTabName> = [FormTabNames.PlanSettings, FormTabNames.DigitalVideoFormatSettings];
  isNew: boolean;
  isCopy: boolean = false;
  hasDefaults: boolean = false;
  planSettingsElement: any;
  digitalVideoFormatSettingsElement: any;
  planVOConfig: PlanConfigVO;
  advertiserInfo: any;
  defaultPlanConfigVO: PlanConfigVO;
  errorData: errorResponse;
  private resolvedDataSubscription: Subscription;
  submitted: boolean = false;
  isSaving: boolean = false;
  planStatus: any = PlanStatus;
  digitalVideoVO: DigitalPlanConfigVO;

  constructor(
    private plannerService: PlannerService,
    private url: LocationStrategy,
    private route: ActivatedRoute,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private dataService: DataService,
    private modal: ModalService,
    private storageService: StorageService
  ) {
    this.planForm = this.getPlanForm();
    this.checkChannelValueChange();
  }

  ngOnInit() {
    this.isCopy = this.url.path().includes("/copy");
    this.isNew = this.url.path().includes("/planner/new");

    let data = this.route.snapshot.data;
    if (data.planConfigVO && !this.isNew) {
      this.planVOConfig = data.planConfigVO;
      if (!data.advertiserInfo) {
        data.advertiserInfo = {
          id: this.planVOConfig.advertiserId,
          marketId: this.planVOConfig.marketId,
          name: this.planVOConfig.advertiserName
        }
      }
      if (this.isCopy) {
        this.planVOConfig.id = null;
        this.planVOConfig.name += "-copy";
        this.planVOConfig.status = PlanStatus.Draft.key;
      }
      this.planForm.patchValue(this.planVOConfig);
      this.hasDigitalVideo = _.includes(this.planForm.get('channels').value, PlannerEditComponent.DigitalVideo);
      if (this.hasDigitalVideo) {
        this.digitalVideoVO = this.planVOConfig.channelPlan.digital;
      }
      if (this.plannerService.isPlanFormEditable(this.status.value)) {
        this.planForm.disable();
      }
    }

    if (this.isNew && data.defaultPlanConfigVO) {
      this.hasDefaults = true;
      this.defaultPlanConfigVO = pruneEmpty(data.defaultPlanConfigVO);
      this.planForm.patchValue(this.defaultPlanConfigVO);
    }

    if (data.advertiserInfo) {
      this.advertiserInfo = data.advertiserInfo;
      this.breadcrumbService.prepareBreadcrumb(this.advertiserInfo.marketId, this.advertiserInfo.id);
      this.advertiserInfo.marketName = this.planForm.get('marketName').value;
      this.advertiserInfo.marketTimeZone = this.planForm.get('marketTimeZone').value;
      this.advertiserInfo.currency = this.planForm.get('currency').value;
      this.storageService.setItem(Constants.MARKET_TIMEZONE, this.advertiserInfo.marketTimeZone);
    }

    this.formTitle = this.isNew || this.isCopy ? "New Plan" : "Edit Plan";
  }

  checkChannelValueChange(): void {
    this.planForm.get('channels').valueChanges.subscribe(selectedValue => {
      this.hasDigitalVideo = _.includes(selectedValue, PlannerEditComponent.DigitalVideo);
      if (!this.hasDigitalVideo) {
        this.tabNames = [FormTabNames.PlanSettings];
      } else {
        this.tabNames = [FormTabNames.PlanSettings, FormTabNames.DigitalVideoFormatSettings];
      }
    });
  }

  getPlanForm(): FormGroup {
    return _.cloneDeep(this.plannerService.PlannerDTO);
  }

  ngOnDestroy() {
    if (this.resolvedDataSubscription) {
      this.resolvedDataSubscription.unsubscribe();
    }
  }

  private redirectOnSave(planConfigVO) {
    this.router.navigate([DashboardType.AdvertiserDashboard, planConfigVO.advertiserId], {
      queryParams: {
        marketId: planConfigVO.marketId
      }
    });
  }

  checkPlanConfigBeforeSave(planToSave: PlanConfigVO) {
    if (planToSave.channelPlan.digital.tactics[0].frequencyCap.period === undefined || planToSave.channelPlan.digital.tactics[0].frequencyCap.value === undefined) {
      planToSave.channelPlan.digital.tactics[0].frequencyCap = null;
    }
    if (_.isEmpty(_.intersection(planToSave.channelPlan.digital.tactics[0].environments, PlannerConstants.appEnvList))) {
      planToSave.channelPlan.digital.tactics[0].appLists = [];
    }
    let genderConst = GenderConst[planToSave.channelPlan.digital.tactics[0].demoSetting.gender];
    planToSave.channelPlan.digital.tactics[0].demoSetting.gender = genderConst ? genderConst.value : [];
  }

  validateAndSavePlan(): void {
    this.submitted = true;
    this.planForm.markAllAsTouched();
    if (this.planForm.invalid) {
      this.modal.error({ message: 'Please resolve the validation errors marked in red before saving.' });
      return;
    }
    let planToSave: PlanConfigVO = this.planForm.value;
    if (planToSave) {
      this.checkPlanConfigBeforeSave(planToSave);
      this.planForm.disable();
      this.isSaving = true;
      this.dataService.savePlan(planToSave).then(
        planConfigVO => {
          this.redirectOnSave(planConfigVO);
        },
        error => {
          this.isSaving = false;
          this.planForm.enable();
          this.showDeepLinkingErrors(error);
        }
      );
    }
  }

  showDeepLinkingErrors(error): void {
    this.errorData = (error && !_.isEmpty(error.error)) ? error.error : "";
  }

  get digitalPlan() {
    return this.planForm.get('channelPlan').get('digital') as FormGroup;
  }

  get status(): FormControl {
    return this.planForm.get('status') as FormControl;
  }
}
